/**
 * How a state is SHOWN, in one place, plus the formatters that travel with it.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: a reader must never mistake an
 * incomplete recording for a complete one, or a session we stopped observing for
 * one we watched end. Both failures are silent, both produce an expensive wrong
 * belief, and colour on its own will not carry either — so every pill here has a
 * DISTINCT WORD as well as a distinct colour, and index.css gives the two weak
 * evidence states different border styles and slants so they survive a
 * black-and-white screenshot in a support ticket and a red/green deficiency.
 *
 * PARTIAL AND UNVERIFIED ARE NOT THE SAME CLAIM and are never rendered the same
 * way. PARTIAL means we measured a gap and know its size. UNVERIFIED means we
 * could not measure — which happens precisely when the Durable Object restarted,
 * i.e. exactly when the recording is ALSO most likely damaged. One amber pill for
 * both would destroy the distinction the whole evidence pipeline exists to draw.
 *
 * The `title` on each pill is not decoration either. These are eleven enum values
 * a first-time reader has never seen, and the alternative to a tooltip is a
 * legend on every screen.
 *
 * FORMATTERS LIVE HERE TOO, next to the states, because they are governed by the
 * same honesty rule and because every one of them is about not overstating a
 * number. `fmtCost` never parses money into a float; `fmtMs` never invents
 * precision it does not have; `fmtDuration` returns a dash rather than "0s" for a
 * call whose duration was never measured.
 */
// The `*Icon` aliases rather than the bare names: this lucide major renamed a
// number of icons (AlertTriangle -> TriangleAlert and friends) and kept the
// suffixed form for every icon in both naming eras, so it is the spelling that
// cannot be wrong. ui/sonner.tsx uses the same convention.
import { GlobeIcon, PhoneIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EvidenceState, RecordingState, SessionState } from '@voiceai/wire'

// ---------------------------------------------------------------------------
// Session state
// ---------------------------------------------------------------------------

/**
 * The label and the explanation for each session state.
 *
 * The three LIVE_DEGRADED_* labels all begin "live" on purpose. The conversation
 * really is still happening in all three — that is the property this whole
 * architecture was built to have — and a label that read "degraded" alone would
 * suggest the caller is hearing something wrong. What is degraded is OURS.
 */
const SESSION_TEXT: Record<SessionState, { label: string; title: string }> = {
  CREATED: {
    label: 'created',
    title: 'A session row exists. The SDP exchange has not completed yet.',
  },
  LIVE: {
    label: 'live',
    title: 'Media up, sideband attached, control socket attached.',
  },
  LIVE_DEGRADED_NO_SIDEBAND: {
    label: 'live · no sideband',
    title:
      'The conversation is continuing — media is browser-to-OpenAI and we are not in it. '
      + 'What we have lost is our own second WebSocket into the session: no tool execution '
      + 'and no transcript capture until it re-attaches. Expect a transcript gap here.',
  },
  LIVE_DEGRADED_NO_CONTROL: {
    label: 'live · no control',
    title:
      'The conversation is continuing and the sideband is attached. The browser control '
      + 'socket is gone, so the UI is stale and recording chunks are buffering locally in '
      + 'OPFS. With staging that is latency, not loss.',
  },
  LIVE_DEGRADED_NO_MEDIA_UPLINK: {
    label: 'live · no media uplink',
    title:
      'The conversation and the control channel are both fine. The binary recording uplink '
      + 'is down, so audio chunks are accumulating in the browser instead of reaching S3.',
  },
  ENDED_NORMAL: {
    label: 'ended',
    title: 'We observed the end of this session.',
  },
  ENDED_TRANSFERRED: {
    label: 'transferred',
    title:
      'THE CALLER WAS HANDED TO A HUMAN, and this session ended because of it — mid-'
      + 'conversation, on purpose. The caller never left the line: the carrier kept their leg '
      + 'up and dialled a person. What we can tell you about what happened next is ONE AUDIO '
      + 'FILE and nothing else — no transcript, no turn timeline and no cost, because our '
      + 'view into the conversation ended with the model\u2019s leg. Which desk took it is in '
      + 'the state detail; whether anybody actually picked up is a separate event, because '
      + 'handing a call over and the handover being answered are two different facts.',
  },
  TAKEN_OVER: {
    label: 'live · person on the line',
    title:
      'A PERSON TOOK THIS CALL OVER from the panel. The AI leg was hung up and the caller is '
      + 'now talking to that person, through this call\u2019s Durable Object. Who took it is in '
      + 'the state detail. Nothing after the takeover has model turns, tokens or cost behind '
      + 'it; the conversation is recorded server-side as its own track.',
  },
  ENDED_TAKEN_OVER: {
    label: 'ended · taken over',
    title:
      'This call ended while a person held it. It did not finish with the AI: the part after '
      + 'the takeover is one server-recorded audio track (`takeover`), with no model turns, '
      + 'tokens or cost behind it.',
  },
  ENDED_UNKNOWN: {
    label: 'ended, unobserved',
    title:
      'WE NEVER SAW THIS ONE END. There is no WebSocket CLOSE frame when a Realtime call '
      + 'finishes — the socket simply EOFs — so an intentional hangup and a network fault '
      + 'are indistinguishable until a re-attach returns 404. The sweeper resolved this by '
      + 'that route, or gave up. It is a normal state, not an error, and it is not '
      + '"ended".',
  },
  FAILED: {
    label: 'failed',
    title: 'The session did not reach a working conversation.',
  },
}

/** Is the conversation still happening? Drives polling, and nothing else. */
export function isLive(state: SessionState): boolean {
  return state === 'LIVE' || state.startsWith('LIVE_DEGRADED') || state === 'TAKEN_OVER'
}

export function StatePill({ state, className }: { state: SessionState; className?: string }) {
  // Guarded for the same reason as EvidencePill — see the note above
  // `unknownState`. `unknownState` is a hoisted function declaration, so being
  // defined lower in the file is deliberate, not an accident of ordering: the
  // guard belongs next to the map it protects.
  const known = Object.hasOwn(SESSION_TEXT, state)
  const t = known ? SESSION_TEXT[state] : unknownState(String(state))
  return (
    <span className={cn('pill', known ? `st-${state}` : 'pill-plain', className)} title={t.title}>
      {/* The dot animates only while a call is genuinely up, so a still frame of
          the list is readable: a pulsing dot means "right now". */}
      {isLive(state) && <span className="live-dot mr-1 align-middle" />}
      {t.label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Evidence state
// ---------------------------------------------------------------------------

const EVIDENCE_TEXT: Record<EvidenceState, { label: string; title: string }> = {
  PENDING: {
    label: 'pending',
    title: 'Finalisation has not run yet. Pending is not a defect.',
  },
  COMPLETE: {
    label: 'COMPLETE',
    /**
     * THIS SAID "two independent oracles agree within 2%" AND THAT WAS SELF-REFUTING
     * ON THE SCREEN IT APPEARS ON. Measured on session e5f7a6ca: the assistant
     * track's two oracles read 63.9s (tokens) and 103.2s (RTP span) — 61% apart —
     * with this pill sitting directly above a paragraph printing both numbers. The
     * two oracles DO NOT MEASURE THE SAME QUANTITY, which is the whole argument in
     * workers/jobs/src/completeness.ts: each track gets ONE primary ruler and the
     * other oracle is used only as a bound, in a direction that is inverted between
     * the tracks. `completenessSentence` in SessionDetail.tsx was corrected for
     * exactly this; the pill kept the old claim, so the page contradicted itself in
     * two places on every COMPLETE track.
     *
     * Also per-track, not per-session: the session's verdict is the WORST of its
     * tracks, and only of the tracks that exist.
     */
    title:
      'Every chunk range arrived, and what we hold matches this track’s primary ruler '
      + 'within 2% — the provider’s audio-token accounting for the assistant track, the '
      + 'browser’s RTP timeline for the caller’s. The other oracle is a bound, not a '
      + 'second opinion: the two measure different quantities and are expected to '
      + 'differ. Read the per-track sentence on the session page for what was actually '
      + 'weighed.',
  },
  PARTIAL: {
    label: 'PARTIAL',
    title:
      'A MEASURED gap. We know something is missing and we know how much. The gaps are '
      + 'listed on the session page with their sizes and reasons.',
  },
  UNVERIFIED: {
    label: 'UNVERIFIED',
    title:
      'WE COULD NOT MEASURE. Not the same as PARTIAL: the oracles were unavailable or '
      + 'disagreed, which happens when the Durable Object restarted — the same event '
      + 'that most often damages the recording. Treat the audio as of unknown '
      + 'completeness.',
  },
  ABSENT: {
    label: 'absent',
    title: 'Nothing was captured for this track at all.',
  },
}

/**
 * These three maps are keyed by a union type, so TypeScript believes every lookup
 * hits — and `noUncheckedIndexedAccess` does not apply to a `Record<Union, V>`
 * read with a value of that union. But the value does not come from TypeScript.
 * It arrives as a JSON string from a worker that deploys INDEPENDENTLY of this
 * bundle, so the one case the compiler cannot see is the one that actually
 * happens: the DO gains a state, the page has not been rebuilt, and
 * `EVIDENCE_TEXT[unknown].title` throws. That took out the whole session view,
 * not the pill — a blank screen where the honest answer was "a state this build
 * does not know".
 *
 * So every lookup below is guarded and an unrecognised value renders AS an
 * unrecognised value. Rendering it as PENDING or COMPLETE would be the one thing
 * docs/next-tasks.md §6 forbids: never default an evidence claim.
 */
function unknownState(raw: string): { label: string; title: string } {
  return {
    label: raw === '' ? 'unreported' : raw,
    title:
      'This build does not recognise the state '
      + (raw === '' ? '(empty)' : `"${raw}"`)
      + '. It came from a worker deployed separately from this page, so treat it as '
      + 'unknown — not as pending, and not as complete.',
  }
}

export function EvidencePill({
  evidence,
  className,
}: {
  evidence: EvidenceState
  className?: string
}) {
  const known = Object.hasOwn(EVIDENCE_TEXT, evidence)
  const t = known ? EVIDENCE_TEXT[evidence] : unknownState(String(evidence))
  return (
    <span
      className={cn('pill', known ? `ev-${evidence}` : 'pill-plain', className)}
      title={t.title}
    >
      {t.label}
    </span>
  )
}

/** One sentence stating the claim, for a place where a pill is too terse. */
export function evidenceSentence(evidence: EvidenceState): string {
  return Object.hasOwn(EVIDENCE_TEXT, evidence)
    ? EVIDENCE_TEXT[evidence].title
    : unknownState(String(evidence)).title
}

// ---------------------------------------------------------------------------
// Recording (pipeline) state
// ---------------------------------------------------------------------------

/**
 * WHERE THE ARTEFACT IS, which is a different question from what we can claim
 * about it — hence a third enum and a deliberately plain pill. index.css gives
 * evidence and session states their own colours; giving pipeline position one
 * too would put three coloured things in one table cell and imply they rank.
 */
const RECORDING_TEXT: Record<RecordingState, string> = {
  RECORDING: 'Chunks are still arriving.',
  FINALIZING: 'The jobs worker is stitching and reconciling.',
  READY: 'The object is written and playable.',
  FAILED: 'Finalisation failed. Any audio held is not addressable.',
}

export function RecordingPill({
  state,
  className,
}: {
  state: RecordingState
  className?: string
}) {
  return (
    <span
      className={cn('pill pill-plain', className)}
      title={
        Object.hasOwn(RECORDING_TEXT, state)
          ? RECORDING_TEXT[state]
          : unknownState(String(state)).title
      }
    >
      {String(state).toLowerCase()}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Audio storage — the sessions LIST's simpler question
// ---------------------------------------------------------------------------

/**
 * Are the audio files stored? Three answers — processed, pending, absent —
 * because the list is scanned, not read, and "COMPLETE vs UNVERIFIED" is a
 * claim about completeness, not about whether there is anything to play. The
 * full evidence verdict stays on the session page.
 *
 * Derived, not a fourth enum on the wire: the pipeline state says where the
 * artefact is, and ABSENT evidence says there was nothing to capture. The one
 * conflation this makes is deliberate and the tooltip owns it — the session's
 * evidence is the WORST of its tracks, so "absent" can mean "at least one
 * track has nothing" on a session whose other track is held.
 */
function audioStorage(
  evidence: EvidenceState,
  recording: RecordingState,
): { cls: string; label: string; title: string } | null {
  if (evidence === 'ABSENT') {
    return {
      cls: 'rec-absent',
      label: 'absent',
      title:
        'No audio is stored for at least one of this session’s tracks — often the whole '
        + 'call. The session page lists what each track holds.',
    };
  }
  switch (recording) {
    case 'FAILED':
      return {
        cls: 'rec-absent',
        label: 'absent',
        title: 'Finalisation failed, so no playable audio object was written.',
      };
    case 'READY':
      return {
        cls: 'rec-processed',
        label: 'processed',
        title: 'The audio files are written and playable. Open the session to listen.',
      };
    case 'RECORDING':
    case 'FINALIZING':
      return {
        cls: 'rec-pending',
        label: 'pending',
        title:
          'Audio is still arriving, or the jobs worker has not finished stitching it into '
          + 'playable files. Pending is not a defect.',
      };
    default:
      // A state this build does not know, from a worker deployed separately.
      // Never defaulted to "stored" or "missing" — same rule as the maps above.
      return null;
  }
}

export function AudioPill({
  evidence,
  recording,
  className,
}: {
  evidence: EvidenceState;
  recording: RecordingState;
  className?: string;
}) {
  const t = audioStorage(evidence, recording);
  if (t === null) {
    const u = unknownState(String(recording));
    return (
      <span className={cn('pill pill-plain', className)} title={u.title}>
        {u.label}
      </span>
    );
  }
  return (
    <span className={cn('pill', t.cls, className)} title={t.title}>
      {t.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/**
 * An icon, because this column is scanned rather than read.
 *
 * The two transports differ in exactly one architecturally interesting way and
 * the tooltip says which: on the phone path the recording is carrier-attested
 * and needs no client, on the web path the browser is the only custodian of
 * assistant audio. That is the difference that decides how much a recording is
 * worth, so it belongs on the icon rather than in a footnote.
 */
export function TransportIcon({
  transport,
  className,
}: {
  transport: 'webrtc' | 'sip'
  className?: string
}) {
  if (transport === 'sip') {
    return (
      <span
        className={cn('inline-flex items-center gap-1', className)}
        title="Phone. Carrier to OpenAI over SIP; the recording is Twilio dual-channel and needs no client."
      >
        <PhoneIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-xs text-muted-foreground">phone</span>
      </span>
    )
  }
  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      title="Browser. WebRTC direct to OpenAI; the browser is the only custodian of assistant audio."
    >
      <GlobeIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="text-xs text-muted-foreground">web</span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Formatters
//
// Every one of these returns EM DASH for absent rather than a zero. A call whose
// duration was never measured is not a zero-second call, and a session with no
// cost row is not a free one.
// ---------------------------------------------------------------------------

export const DASH = '—'

/** A call length. Minutes and seconds up to an hour, because the 60-minute
 *  provider session cap means nothing here is ever longer than that plus slop. */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null) return DASH
  if (ms < 1000) return `${ms} ms`
  const total = Math.round(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`
  return `${s}s`
}

/**
 * A latency. Milliseconds below ten seconds, seconds above.
 *
 * No decimals below a second: these are intervals between two WebSocket message
 * arrivals in a Worker where `Date.now()` does not advance during synchronous
 * execution, so the honest resolution is one millisecond and a `.5` would be
 * invented precision.
 */
export function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return DASH
  if (Math.abs(ms) >= 10_000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.round(ms)} ms`
}

/** Seconds, for a media duration where the unit a reader thinks in is seconds. */
export function fmtSeconds(ms: number | null | undefined): string {
  if (ms == null) return DASH
  return `${(ms / 1000).toFixed(1)}s`
}

/**
 * Money, WITHOUT EVER PARSING IT.
 *
 * `costUsd` is `numeric(12,6)` and arrives as a string exactly so that a float
 * never touches it. `Number(s).toFixed(4)` would work for every value this POC
 * will ever produce and would still be the wrong habit to establish in the file
 * every screen imports, so this is string surgery: keep at most four decimal
 * places, trim trailing zeros back to two, group nothing (nothing here reaches a
 * thousand dollars).
 */
export function fmtCost(usd: string | null | undefined): string {
  if (usd == null || usd === '') return DASH
  const neg = usd.startsWith('-')
  const body = neg ? usd.slice(1) : usd
  const dot = body.indexOf('.')
  const whole = dot === -1 ? body : body.slice(0, dot)
  let frac = dot === -1 ? '' : body.slice(dot + 1, dot + 5)
  while (frac.length > 2 && frac.endsWith('0')) frac = frac.slice(0, -1)
  while (frac.length < 2) frac += '0'
  return `${neg ? '-' : ''}$${whole || '0'}.${frac}`
}

/** A timestamp a human reads. en-GB, so the day comes before the month and a
 *  reader in Europe does not silently misread 03/04 as April. */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return DASH
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return DASH
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** Epoch millis from the DO's clock. Time of day only — the date is in the
 *  header and repeating it on every transcript line is noise. */
export function fmtClock(atMs: number | null | undefined): string {
  if (atMs == null) return DASH
  return new Date(atMs).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** A completeness ratio as a percentage. NULL is not 0% — it is "unmeasured",
 *  and the caller must say so in words rather than printing a number. */
export function fmtPct(ratio: number | null | undefined): string {
  if (ratio == null) return DASH
  return `${(ratio * 100).toFixed(ratio >= 0.995 || ratio < 0.1 ? 1 : 0)}%`
}

export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes == null) return DASH
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Integers in a table. Grouped, tabular numerals come from `.num` in index.css. */
export function fmtInt(n: number | null | undefined): string {
  if (n == null) return DASH
  return n.toLocaleString('en-GB')
}
